/**
 * CategoriesPage — products for the category chosen in the header strip.
 *
 * Category selection now lives in the sticky header's CategoryScroller (the same
 * horizontal strip as the home page). This page simply lists the products for
 * whatever category is open via ?category=<id> — or ALL products when none is
 * selected ("All" tab). Tapping a tab in the header re-filters this list.
 */

import { useSearchParams } from 'react-router-dom';
import { PackageOpen } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductCardSkeleton, EmptyState, ErrorState, Spinner } from '@/components/ui';
import { useCollege } from '@/context/CollegeContext';
import { useCategories, useProducts } from '@/hooks/useStorefront';

export function CategoriesPage() {
  const [searchParams] = useSearchParams();
  const activeCategory = searchParams.get('category');
  const { collegeId } = useCollege();

  const { data: categories = [] } = useCategories(collegeId);
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useProducts({ collegeId, category: activeCategory ?? undefined, limit: 30 });

  const products = data?.items ?? [];
  const title = activeCategory
    ? (categories.find((c) => c._id === activeCategory)?.name ?? 'Category')
    : 'All products';

  return (
    <Container className="pt-4">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-lg font-extrabold tracking-tight text-ink">{title}</h2>
        {isFetching && !isLoading && <Spinner size="sm" className="text-primary-600" />}
      </div>

      {isError ? (
        <ErrorState error={error} onRetry={refetch} className="min-h-[50vh]" />
      ) : isLoading ? (
        <div className="grid grid-cols-3 gap-x-2.5 gap-y-5 sm:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          icon={PackageOpen}
          title="No products here yet"
          description="Nothing in this category right now — try another tab above."
          className="min-h-[45vh]"
        />
      ) : (
        <div className="grid grid-cols-3 gap-x-2.5 gap-y-5 sm:grid-cols-4 lg:grid-cols-6">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </Container>
  );
}

export default CategoriesPage;
